import { Pencil, Trash2, UserX, UserCheck } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { mediciAPI } from '../../services/api';
import MedicoForm from './MedicoForm';
import { useAuthStore } from '../../store/useStore';
import '../../styles/MediciList.css';

const MediciList = () => {
  /* L'elenco dei medici lo legge chiunque - serve per prenotare e per
     scegliere l'esecutore - ma la scheda la scrivono amministrazione e
     segreteria, e cancellare un medico e' solo dell'amministratore.
     Sono le stesse regole delle rotte: qui si nascondono i pulsanti che
     porterebbero a un rifiuto. */
  const ruolo = useAuthStore((s) => s.user?.ruolo);
  const puoModificare = ['admin', 'segreteria'].includes(ruolo);
  const puoEliminare = ruolo === 'admin';
  const [medici, setMedici] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avviso, setAvviso] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAttivo, setFilterAttivo] = useState('tutti'); // tutti | attivi | inattivi
  const [showForm, setShowForm] = useState(false);
  const [selectedMedico, setSelectedMedico] = useState(null);

  /* Arrivando dal compito «iscrizione incompleta» (o da qualunque
     rimando che porti un medicoId), la scheda di quel medico si apre da
     sola: fra il leggere e l'agire non ci sono altri passaggi. */
  const { state: statoNavigazione } = useLocation();
  const daAprire = statoNavigazione?.medicoId || null;

  useEffect(() => {
    loadMedici();
    loadScadenze();
  }, []);

  useEffect(() => {
    if (!daAprire || !medici.length || showForm) return;
    const m = medici.find((x) => Number(x.id) === Number(daAprire));
    if (m) { setSelectedMedico(m); setShowForm(true); }
    // eslint-disable-next-line
  }, [daAprire, medici]);

  const loadMedici = async () => {
    try {
      setLoading(true);
      const response = await mediciAPI.getAll();
      setMedici(response.data?.data?.medici || []);
      setError(null);
    } catch (err) {
      console.error('Errore caricamento medici:', err);
      setError('Errore nel caricamento dei medici');
    } finally {
      setLoading(false);
    }
  };

  const loadScadenze = async () => {
    try {
      const response = await mediciAPI.getScadenze();
      setScadenze(response.data?.data?.medici || []);
    } catch (err) {
      console.error('Errore caricamento scadenze:', err);
    }
  };

  const handleNew = () => {
    setSelectedMedico(null);
    setShowForm(true);
  };

  const handleEdit = (medico) => {
    setSelectedMedico(medico);
    setShowForm(true);
  };

  /* UN MEDICO CON UNA STORIA NON SI CANCELLA: si disattiva. Il tasto
     «Elimina» compare solo a chi non ha niente dietro (ha_storia dal
     server); per tutti gli altri c'e' «Disattiva», che toglie il medico
     dai selezionabili per le cose nuove e lo lascia intatto ovunque sia
     gia' citato. Il server ricontrolla comunque tutte le tabelle e, se
     rifiuta, risponde col motivo e i numeri: quel messaggio si mostra
     qui, con il meccanismo dell'applicazione - un alert() del browser in
     alcuni contesti non compare affatto, e il tasto sembra non
     funzionare. */
  const messaggioDi = (err, ripiego) => err?.response?.data?.message || err?.message || ripiego;

  const handleDelete = async (medico) => {
    const conferma = window.confirm(
      `Eliminare ${medico.cognome} ${medico.nome} dall'anagrafica?\n\n`
      + 'Questo medico non ha procedure, prestazioni, agende, fatture o altro collegato: '
      + 'la scheda viene tolta del tutto, e non c\'è un cestino da cui recuperarla.'
    );
    if (!conferma) return;
    setError(null);
    setAvviso(null);
    try {
      await mediciAPI.delete(medico.id);
      setAvviso(`${medico.cognome} ${medico.nome} eliminato dall'anagrafica.`);
      await loadMedici();
      await loadScadenze();
    } catch (err) {
      setError(messaggioDi(err, 'Errore durante l\'eliminazione del medico'));
    }
  };

  const handleAttivo = async (medico, attivo) => {
    const conferma = window.confirm(attivo
      ? `Riattivare ${medico.cognome} ${medico.nome}?\n\nTorna selezionabile per procedure, agende e prestazioni nuove.`
      : `Disattivare ${medico.cognome} ${medico.nome}?\n\n`
        + 'Non comparirà più fra i medici selezionabili per procedure, agende e prestazioni nuove. '
        + 'Tutto quello che ha già fatto resta com\'è, a suo nome: le procedure, le prestazioni, '
        + 'le fatture e le spettanze non cambiano.');
    if (!conferma) return;
    setError(null);
    setAvviso(null);
    try {
      await mediciAPI.update(medico.id, { attivo });
      setAvviso(`${medico.cognome} ${medico.nome} ${attivo ? 'riattivato' : 'disattivato'}.`);
      await loadMedici();
    } catch (err) {
      setError(messaggioDi(err, 'Errore nell\'aggiornamento del medico'));
    }
  };

  const handleFormSave = async () => {
    setShowForm(false);
    setSelectedMedico(null);
    await loadMedici();
    await loadScadenze();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedMedico(null);
  };

  // Calcola stato scadenza assicurazione
  const getScadenzaStatus = (dataScadenza) => {
    if (!dataScadenza) return null;
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    const scadenza = new Date(dataScadenza);
    const diffGiorni = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));

    if (diffGiorni < 0) return { tipo: 'scaduta', label: 'Scaduta', giorni: diffGiorni };
    if (diffGiorni <= 30) return { tipo: 'critica', label: `${diffGiorni} gg`, giorni: diffGiorni };
    if (diffGiorni <= 60) return { tipo: 'attenzione', label: `${diffGiorni} gg`, giorni: diffGiorni };
    return null;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT');
  };

  // Applica filtri
  const filteredMedici = medici.filter((m) => {
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch =
      term === '' ||
      `${m.nome} ${m.cognome}`.toLowerCase().includes(term) ||
      `${m.cognome} ${m.nome}`.toLowerCase().includes(term) ||
      (m.specializzazione && m.specializzazione.toLowerCase().includes(term)) ||
      (m.codice_fiscale && m.codice_fiscale.toLowerCase().includes(term)) ||
      (m.numero_ordine && String(m.numero_ordine).toLowerCase().includes(term));

    const matchesAttivo =
      filterAttivo === 'tutti' ||
      (filterAttivo === 'attivi' && m.attivo) ||
      (filterAttivo === 'inattivi' && !m.attivo);

    return matchesSearch && matchesAttivo;
  });

  if (loading) {
    return <div className="medici-loading">Caricamento medici in corso...</div>;
  }

  return (
    <div className="medici-container">
      {/* Header */}
      <div className="medici-header">
        <h2>Gestione medici</h2>
        {puoModificare && (
          <button className="btn-primary" onClick={handleNew}>
            Nuovo medico
          </button>
        )}
      </div>

      {/* Alert assicurazioni in scadenza */}
      {scadenze && scadenze.length > 0 && (
        <div className="alert-scadenze">
          <h3>Assicurazioni in scadenza o scadute</h3>
          <ul className="scadenze-list">
            {scadenze.map((s) => {
              const status = getScadenzaStatus(s.assicurazione_data_scadenza);
              return (
                <li key={s.id} className={`scadenza-item ${status?.tipo || ''}`}>
                  <span className="scadenza-medico">
                    Dr. {s.cognome} {s.nome}
                  </span>
                  {s.assicurazione_compagnia && (
                    <span className="scadenza-compagnia"> — {s.assicurazione_compagnia}</span>
                  )}
                  <span className="scadenza-data">
                    Scadenza: <strong>{formatDate(s.assicurazione_data_scadenza)}</strong>
                  </span>
                  {status && (
                    <span className={`badge badge-${status.tipo}`}>{status.label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Toolbar filtri */}
      <div className="medici-toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="Nome, specializzazione, n° ordine…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="filter-select"
          value={filterAttivo}
          onChange={(e) => setFilterAttivo(e.target.value)}
        >
          <option value="tutti">Tutti i medici</option>
          <option value="attivi">Solo attivi</option>
          <option value="inattivi">Solo inattivi</option>
        </select>
        <div className="medici-count">
          {filteredMedici.length} {filteredMedici.length === 1 ? 'medico' : 'medici'}
        </div>
      </div>

      {error && <div className="rm-messaggio rm-messaggio-errore">{error}</div>}
      {avviso && <div className="rm-messaggio rm-messaggio-conferma">{avviso}</div>}

      {/* Tabella */}
      {filteredMedici.length === 0 ? (
        <div className="no-results">
          {medici.length === 0
            ? 'Nessun medico inserito. Clicca su "Nuovo medico" per aggiungerne uno.'
            : 'Nessun medico trovato con i filtri attuali.'}
        </div>
      ) : (
        <div className="medici-table-wrapper">
          <table className="medici-table">
            <thead>
              <tr>
                <th>Nome e specializzazione</th>
                <th>Iscrizione all'albo</th>
                <th>Contatti</th>
                <th>% Spettanza</th>
                <th>Assicurazione</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredMedici.map((m) => {
                const scadenzaStatus = getScadenzaStatus(m.assicurazione_data_scadenza);
                return (
                  <tr key={m.id} className={!m.attivo ? 'medico-inattivo' : ''}>
                    {/* La specializzazione sta SOTTO IL NOME, non in una
                        colonna sua: con otto colonne la tabella arrivava a
                        1327px e le azioni finivano fuori dallo schermo anche
                        a 1440 (misurato il 22 settembre 2026). E' un
                        attributo della persona, si legge col nome. */}
                    <td className="nome-cell">
                      <strong>Dr. {m.cognome} {m.nome}</strong>
                      {m.specializzazione && (
                        <div className="specializzazione-riga">{m.specializzazione}</div>
                      )}
                      {m.codice_fiscale && (
                        <div className="codice-fiscale">{m.codice_fiscale}</div>
                      )}
                    </td>
                    <td className="iscrizione-cell">
                      {(() => {
                        const p = (m.iscrizioni || []).find((i) => i.principale) || (m.iscrizioni || [])[0];
                        if (!p) return <span className="iscrizione-manca">nessuna</span>;
                        if (p.completa) return <span title={p.riga}>{p.albo_nome} {p.sede_nome} n. {p.numero}</span>;
                        return <span className="iscrizione-manca" title={`Manca: ${p.mancanti.join(', ')}`}>
                          n. {p.numero} — incompleta</span>;
                      })()}
                    </td>
                    <td className="contatti-cell">
                      {m.email && <div className="contatto">{m.email}</div>}
                      {m.telefono && <div className="contatto">{m.telefono}</div>}
                      {!m.email && !m.telefono && '-'}
                    </td>
                    <td className="text-center">
                      {m.percentuale_spettanza != null && m.percentuale_spettanza !== ''
                        ? `${m.percentuale_spettanza}%`
                        : '-'}
                    </td>
                    <td>
                      {m.assicurazione_compagnia || m.assicurazione_data_scadenza ? (
                        <div className="assicurazione-cell">
                          {m.assicurazione_compagnia && (
                            <div className="compagnia">{m.assicurazione_compagnia}</div>
                          )}
                          {m.assicurazione_numero_polizza && (
                            <div className="polizza">N° {m.assicurazione_numero_polizza}</div>
                          )}
                          {m.assicurazione_data_scadenza && (
                            <div className="data-piccola">
                              Scad: {formatDate(m.assicurazione_data_scadenza)}
                              {scadenzaStatus && (
                                <span className={`badge badge-${scadenzaStatus.tipo}`}>
                                  {scadenzaStatus.label}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`stato-badge ${m.attivo ? 'attivo' : 'inattivo'}`}>
                        {m.attivo ? 'Attivo' : 'Inattivo'}
                      </span>
                    </td>
                    <td className="azioni-cell">
                      {puoModificare && (
                        <button
                          className="btn-action btn-edit"
                          onClick={() => handleEdit(m)}
                          title="Modifica"
                        ><Pencil size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                      )}
                      {puoEliminare && m.ha_storia && (
                        <button
                          className="btn-action btn-stato"
                          onClick={() => handleAttivo(m, !m.attivo)}
                          title={m.attivo ? 'Disattiva: non più selezionabile per cose nuove, intatto nel passato' : 'Riattiva'}
                        >{m.attivo
                            ? <><UserX size={15} strokeWidth={1.8} aria-hidden="true" /> Disattiva</>
                            : <><UserCheck size={15} strokeWidth={1.8} aria-hidden="true" /> Riattiva</>}</button>
                      )}
                      {puoEliminare && !m.ha_storia && (
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(m)}
                          title="Elimina: solo perché non ha niente collegato"
                        ><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form modale (creato/modificato medico) */}
      {showForm && (
        <MedicoForm
          medico={selectedMedico}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default MediciList;
